import { useEffect, useState, useMemo } from 'react';
import { useAppSelector, ApplicationStatus, IApplication, REQUIRED_APPLICATION_LENGTH } from '../utils';
import { isDateAfter, parseDate } from '@utils/dates/date.utils';

export const useIsActionsButtonAccessible = (application: IApplication): boolean => {
    const [isActionsButtonAccessible, setIsActionsButtonAccessible] = useState(false);

    const userPermissionsList: string[] = useAppSelector(
        state => state.capApplications.capApplicationsInfo.userPermissionList,
    );
    const currentApplications: IApplication[] = useAppSelector(
        state => state.capApplications.capApplicationsInfo.currentApplications,
    );

    const checkActionsButtonAccessibility = useMemo(() => {
        const hasSepDenialOverridePermission = (() => {
            const hasPermission = userPermissionsList.includes('IND_PORTAL_OVERRIDE_SEP_DENIAL');
            const isSepDenied = application.isSepQepDenied;
            const isApplicationClosed = application.applicationStatus === ApplicationStatus.Closed;
            const isWithinApplicationLimit =
                currentApplications && currentApplications.length < REQUIRED_APPLICATION_LENGTH;
            const hasValidCurrentApplicationStatus =
                currentApplications.length === 0 ||
                [ApplicationStatus.EnrolledOrActive, ApplicationStatus.PartiallyEnrolled].includes(
                    currentApplications[0].applicationStatus as ApplicationStatus,
                );
            const isApplicationMoreRecent =
                currentApplications.length === 0 ||
                isDateAfter(parseDate(application.lastUpdated), parseDate(currentApplications[0].lastUpdated));
            return (
                hasPermission &&
                isSepDenied &&
                isApplicationClosed &&
                isWithinApplicationLimit &&
                hasValidCurrentApplicationStatus &&
                isApplicationMoreRecent
            );
        })();

        const hasPlanShoppingWindowPermission = (() => {
            const hasPermission = userPermissionsList.includes('MYAPP_OPEN_PLAN_SHOPPING_WINDOW');
            const isExchangeEligible = application.isExchangeEligible;
            const isLatestApplication = application.isLatestApplication;
            return hasPermission && isExchangeEligible && isLatestApplication;
        })();

        return hasSepDenialOverridePermission || hasPlanShoppingWindowPermission;
    }, [application, currentApplications, userPermissionsList]);

    useEffect(() => {
        setIsActionsButtonAccessible(checkActionsButtonAccessibility);
    }, [checkActionsButtonAccessibility]);

    return isActionsButtonAccessible;
};
